import type { IPayoutCountrySupport } from '../types/global/payout.types';
import { defaultAccountCurrencyForCountry } from './externalAccountDisplay';

export interface PaymentMethodOption {
    id: string;
    name: string;
    description: string;
    externalAccountType: string;
}

const METHOD_LABELS: Record<string, { name: string; description: string }> = {
    nuban: { name: 'NUBAN Transfer', description: 'Nigerian bank account' },
    bank_account: { name: 'Bank Account', description: 'Local bank transfer' },
    mobile_money: { name: 'Mobile Money', description: 'Mobile wallet transfer' },
    mobile_money_business: { name: 'M-Pesa Business', description: 'Paybill or Till number' },
    ach: { name: 'ACH Transfer', description: 'US bank account (ACH)' },
    swift: { name: 'IBAN / SWIFT', description: 'International bank account (IBAN)' },
    paypal: { name: 'PayPal', description: 'PayPal email payout (manual)' },
    wire: { name: 'Wire Transfer', description: 'Manual international bank transfer' },
};

export function paymentMethodsForCountrySupport(support?: IPayoutCountrySupport): PaymentMethodOption[] {
    if (!support) return [];

    if (support.automation === 'automated') {
        return (support.supported_account_types ?? []).map(type => {
            const label = METHOD_LABELS[type] ?? { name: type, description: 'Payout account' };
            return {
                id: type,
                name: label.name,
                description: label.description,
                externalAccountType: type,
            };
        });
    }

    const methods: PaymentMethodOption[] = (support.supported_account_types ?? ['bank_account', 'swift']).map(type => {
        const label = METHOD_LABELS[type === 'swift' ? 'wire' : type] ?? METHOD_LABELS.wire;
        return {
            id: type === 'swift' ? 'wire' : type,
            name: label.name,
            description: label.description,
            externalAccountType: type,
        };
    });

    if (support.alternate_account_types?.includes('paypal')) {
        methods.push({
            id: 'paypal',
            name: METHOD_LABELS.paypal.name,
            description: METHOD_LABELS.paypal.description,
            externalAccountType: 'paypal',
        });
    }

    return methods;
}

export function payoutBanksQueryForMethod(
    country: string,
    methodId: string,
): {
    currency?: string;
    type?: string;
    country?: string;
    enabled_for_verification?: boolean;
    cacheKey: string;
} {
    const c = country.toUpperCase();
    switch (c) {
        case 'NG':
            return { currency: 'NGN', country: c, cacheKey: `${c}-banks` };
        case 'GH':
            if (methodId === 'mobile_money') {
                return { type: 'mobile_money', country: c, cacheKey: `${c}-momo-flw` };
            }
            return { country: c, cacheKey: `${c}-banks-flw` };
        case 'KE':
            if (methodId === 'mobile_money') {
                return { type: 'mobile_money', country: c, cacheKey: `${c}-momo-flw` };
            }
            return { country: c, cacheKey: `${c}-banks-flw` };
        case 'ZA':
            return { country: c, cacheKey: `${c}-banks-flw` };
        // Flutterwave MoMo / bank corridors
        case 'UG':
        case 'TZ':
        case 'RW':
        case 'ZM':
        case 'MW':
        case 'EG':
        case 'BJ':
        case 'BF':
        case 'CI':
        case 'GW':
        case 'ML':
        case 'NE':
        case 'SN':
        case 'TG':
        case 'CM':
        case 'CF':
        case 'TD':
        case 'CG':
        case 'GQ':
        case 'GA':
            if (methodId === 'mobile_money') {
                return { type: 'mobile_money', country: c, cacheKey: `${c}-momo-flw` };
            }
            return { country: c, cacheKey: `${c}-banks-flw` };
        default:
            return { country: c, cacheKey: `${c}-banks` };
    }
}

/** Whether the details step should load /payout/banks for this method. */
export function shouldLoadPayoutBanksForMethod(methodId: string): boolean {
    return ['nuban', 'bank_account', 'mobile_money'].includes(methodId);
}

export function isAutomatedRail(support?: IPayoutCountrySupport): boolean {
    return support?.automation === 'automated';
}

export function isManualWireRail(support?: IPayoutCountrySupport): boolean {
    return support?.payout_rail === 'manual_bank_transfer';
}

/** Bank methods that require a residential address on create/update. */
export function methodNeedsResidentialAddress(methodId?: string): boolean {
    return ['ach', 'nuban', 'bank_account', 'wire', 'swift'].includes(methodId || '');
}

export function hasValidResidentialAddress(address?: string | null): boolean {
    return String(address ?? '').trim().length >= 4;
}

/**
 * Pre-save verify is enforced for NUBAN only (for now).
 * Other rails (ACH, MoMo, Stripe, etc.) can save without client verify.
 */
export function requiresPreSaveAccountVerification(
    _support?: IPayoutCountrySupport,
    paymentMethodId?: string,
): boolean {
    return paymentMethodId === 'nuban';
}

export function isVerifyResultAcceptedForSave(result?: {
    verification_status?: string;
    verified_account_name?: string;
}): boolean {
    if (!result) return false;
    if (result.verification_status === 'failed') return false;
    if (result.verification_status === 'verified') return true;
    // Paystack resolve often returns the matched name without always setting status.
    return Boolean(result.verified_account_name?.trim());
}

export interface ExternalAccountFormData {
    accountHolderName?: string;
    /** Name registered on the mobile-money wallet (MoMo); distinct from display account holder. */
    accountName?: string;
    accountNumber?: string;
    routingNumber?: string;
    bankName?: string;
    bankAddress?: string;
    swiftCode?: string;
    mobileNumber?: string;
    mobileProvider?: string;
    accountType?: 'checking' | 'savings' | 'personal' | 'business';
    documentType?: string;
    documentNumber?: string;
    paypalEmail?: string;
    paypalUsername?: string;
    accountCurrency?: string;
    feeAcknowledged?: boolean;
    momoVariant?: 'personal' | 'paybill' | 'till';
}

export function buildExternalAccountPayload(
    country: string,
    methodId: string,
    formData: ExternalAccountFormData,
    banks: { name: string; code: string }[],
): Record<string, unknown> | null {
    const currency = formData.accountCurrency?.toUpperCase() || defaultAccountCurrencyForCountry(country);

    const base = {
        name: formData.accountHolderName || 'External Account',
        country,
        account_currency: currency,
    };

    switch (methodId) {
        case 'nuban': {
            const bank = banks.find(b => b.name === formData.bankName);
            return {
                ...base,
                external_account_type: 'nuban',
                bank_details: {
                    account_number: formData.accountNumber,
                    bank_name: formData.bankName,
                    bank_code: bank?.code || '',
                    address: formData.bankAddress || '',
                },
            };
        }
        case 'bank_account': {
            const bank = banks.find(b => b.name === formData.bankName);
            if (country === 'GB') {
                return {
                    ...base,
                    external_account_type: 'bank_account',
                    bank_details: {
                        routing_number: String(formData.routingNumber ?? '').replace(/\D/g, ''),
                        account_number: formData.accountNumber,
                        address: formData.bankAddress || '',
                    },
                };
            }
            if (country === 'ZA') {
                return {
                    ...base,
                    external_account_type: 'bank_account',
                    bank_details: {
                        account_number: formData.accountNumber,
                        bank_name: formData.bankName,
                        bank_code: bank?.code || '',
                        account_holder_name: formData.accountHolderName,
                        account_type: formData.accountType,
                        document_type: formData.documentType,
                        document_number: formData.documentNumber,
                        address: formData.bankAddress || '',
                    },
                };
            }
            return {
                ...base,
                external_account_type: 'bank_account',
                bank_details: {
                    account_number: formData.accountNumber,
                    bank_name: formData.bankName,
                    bank_code: bank?.code || '',
                    address: formData.bankAddress || '',
                },
            };
        }
        case 'mobile_money': {
            const provider = banks.find(b => b.name === formData.mobileProvider);
            // Flutterwave codes: KE M-Pesa = MPS (legacy Paystack MPESA aliased server-side).
            const code = provider?.code || (country === 'KE' ? 'MPS' : '');
            const registeredName = formData.accountName?.trim() || formData.accountHolderName || 'Mobile Money';
            return {
                ...base,
                name: formData.accountHolderName || registeredName,
                external_account_type: 'mobile_money',
                mobile_money_details: {
                    phone_number: formData.mobileNumber,
                    name: registeredName,
                    mobile_money_code: code,
                    mobile_money_name: formData.mobileProvider || provider?.name || 'Mobile Money',
                },
            };
        }
        case 'mobile_money_business': {
            let code = 'MPPAYBILL';
            if (formData.momoVariant === 'till') code = 'MPTILL';
            return {
                ...base,
                external_account_type: 'mobile_money_business',
                mobile_money_details: {
                    phone_number: formData.mobileNumber,
                    name: formData.accountHolderName,
                    mobile_money_code: code,
                    mobile_money_name: formData.momoVariant === 'till' ? 'M-PESA Till' : 'M-PESA Paybill',
                },
            };
        }
        case 'ach':
            return {
                ...base,
                external_account_type: 'ach',
                bank_details: {
                    routing_number: String(formData.routingNumber ?? '').trim(),
                    account_number: String(formData.accountNumber ?? '').trim(),
                    address: formData.bankAddress || '',
                    ...(formData.bankName?.trim() ? { bank_name: formData.bankName.trim() } : {}),
                },
            };
        case 'swift':
            return {
                ...base,
                external_account_type: 'swift',
                bank_details: {
                    account_number: String(formData.accountNumber ?? '').trim(),
                    routing_number: formData.swiftCode?.trim() || undefined,
                    address: formData.bankAddress || '',
                },
            };
        case 'wire': {
            const swift = String(formData.swiftCode ?? '')
                .trim()
                .toUpperCase();
            return {
                ...base,
                external_account_type: 'swift',
                bank_details: {
                    bank_name: formData.bankName,
                    account_number: formData.accountNumber,
                    address: formData.bankAddress || '',
                    routing_number: swift,
                },
            };
        }
        case 'paypal':
            return {
                ...base,
                external_account_type: 'paypal',
                paypal_details: {
                    email: formData.paypalEmail,
                    username: formData.paypalUsername || formData.paypalEmail,
                },
            };
        default:
            return null;
    }
}

export function buildVerifyAccountPayload(
    country: string,
    methodId: string,
    formData: ExternalAccountFormData,
    banks: { name: string; code: string }[],
): Record<string, unknown> | null {
    const created = buildExternalAccountPayload(country, methodId, formData, banks);
    if (!created) return null;
    return {
        country: created.country,
        external_account_type: created.external_account_type,
        account_currency: created.account_currency,
        name: created.name,
        bank_details: created.bank_details,
        mobile_money_details: created.mobile_money_details,
    };
}
